import type { SerializeFrom } from '@remix-run/node';
import { WebRoutes, getProductImageUrl, getVariantColour, withDomain } from '@theprintbar/shared';
import type { loader } from '../_index/_index.tsx';

export function getProductStructuredData(data?: SerializeFrom<typeof loader>) {
	if (!data) return {};
	const { product } = data;
	return {
		'script:ld+json': {
			'@context': 'https://schema.org',
			'@type': 'ProductGroup',
			productGroupID: product.code,
			name: product.name,
			brand: {
				'@type': 'Brand',
				name: product.brand.name,
			},
			image: [getProductImageUrl({ product, productCode: product.code, webDomain: data.webDomain })],
			description: `Custom ${product.name} from The Print Bar`,
			variesBy: ['https://schema.org/size', 'https://schema.org/color'],
			hasVariant:
				product.variants?.map(variant => {
					const colour = getVariantColour(product, variant);
					return {
						'@type': 'Product',
						'@id': withDomain(WebRoutes.Product(product.slug), data.webDomain) + `#${variant.code}`,
						isVariantOf: {
							'@id': withDomain(WebRoutes.Product(product.slug), data.webDomain) + '#productgroup',
						},
						sku: variant.code,
						image: getProductImageUrl({ product, productCode: product.code, colourCode: colour?.code, webDomain: data.webDomain }),
						name: product.name,
						color: colour?.name,
						size: variant.size.code,
						offers: {
							'@type': 'Offer',
							priceCurrency: 'AUD',
							price: product.salePrice,
							itemCondition: 'https://schema.org/NewCondition',
							availability: 'https://schema.org/InStock',
							url: withDomain(WebRoutes.Product(product.slug), data.webDomain),
							shippingDetails: { '@id': '#shipping_policy' },
							hasMerchantReturnPolicy: { '@id': '#return_policy' },
						},
					};
				}) || [],
			'@id': withDomain(WebRoutes.Product(product.slug), data.webDomain) + '#productgroup',
			url: withDomain(WebRoutes.Product(product.slug), data.webDomain),
			offers: {
				'@type': 'AggregateOffer',
				lowPrice: product.salePrice,
				highPrice: product.salePrice,
				priceCurrency: 'AUD',
				itemCondition: 'https://schema.org/NewCondition',
				availability: 'https://schema.org/InStock',
				offerCount: product.variants?.length || 0,
				hasMerchantReturnPolicy: {
					'@type': 'MerchantReturnPolicy',
					'@id': '#return_policy',
					applicableCountry: 'AU',
					returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
					merchantReturnLink: withDomain(WebRoutes.HelpCategoryPage('returns-faq'), data.webDomain),
				},
				shippingDetails: {
					'@type': 'OfferShippingDetails',
					'@id': '#shipping_policy',
					shippingDestination: {
						'@type': 'DefinedRegion',
						addressCountry: 'AU',
					},
					shippingSettingsLink: withDomain(WebRoutes.HelpCategoryPage('shipping-ordering-faq'), data.webDomain),
				},
				seller: {
					'@id': withDomain(WebRoutes.Home, data.webDomain) + '/#organization',
				},
			},
		},
	};
}
